import React, { useEffect, useState } from 'react';
import { Employee } from '../../../types';
import { getCompanyWidgetDuty } from '../companyHelpers';
import ConfirmDeleteModal from './ConfirmDeleteModal/ConfirmDeleteModal';
import EmployeeList from './EmployeeList/EmployeeList';
import OwnerItem from './EmployeeItem/OwnerItem';
import ModalHeader from './ModalHeader/ModalHeader';
import style from './ModalWindowEmployees.module.css';
import SearchBar from './SearchBar/SearchBar';
import AzIcon from './icons/AzIcon';
import ZaIcon from './icons/ZaIcon';
import { useDeleteEmployee, useEmployeeData, useSearch } from './utils';

interface ModalWindowEmployeesProps {
  /** §FB37: вкладка сторінки компанії — без оверлею та кнопки закриття. */
  embedded?: boolean;
  selectedClientId: number | null;
  selectedClientName: string;
  /** §FB81: стан master-switch компанії — показує вебперемикачі відповідальних осіб. */
  companyWebLiaisonEnabled?: boolean;
  /** §FB170: вебкабінет викликів працює в системі (глобальний прапорець; перемикача компанії з
   *  27.09.2026 немає — доступ дає роль «Дзвінки з QR») — позначка «на чергуванні» в рядках. */
  callsCabinetEnabled?: boolean;
  /** §FB172: контакти керівника (обліковий запис компанії) — рядок «автоматично» над списком. */
  ownerEmail?: string;
  ownerPhone?: string;
  ownerAvatarUrl?: string;
  onClose: () => void;
  onOpen: () => void;
}

const ModalWindowEmployees: React.FC<ModalWindowEmployeesProps> = ({
  selectedClientId,
  selectedClientName,
  companyWebLiaisonEnabled = false,
  callsCabinetEnabled = false,
  ownerEmail,
  ownerPhone,
  ownerAvatarUrl,
  onClose,
  onOpen,
  embedded = false,
}) => {
  const [orderDirection, setOrderDirection] = useState<'ASC' | 'DESC'>('ASC');
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [employeeToDelete, setEmployeeToDelete] = useState<Employee | null>(
    null,
  );

  const {
    employees,
    hasMore,
    pageIndex,
    setPageIndex,
    fetchEmployees,
    setHasMore,
    isFetching,
  } = useEmployeeData(selectedClientId, orderDirection, '');

  const { searchText, handleSearchChange, handleClearSearch } =
    useSearch(fetchEmployees);

  // §FB170: хто зараз на чергуванні в кабінеті викликів — крапка в рядку співробітника.
  const [onDutyIds, setOnDutyIds] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (!callsCabinetEnabled || selectedClientId === null) {
      setOnDutyIds(new Set());
      return undefined;
    }
    let active = true;
    const load = () =>
      getCompanyWidgetDuty(selectedClientId)
        .then((b) => {
          if (!active) return;
          setOnDutyIds(
            new Set(
              b.people
                .filter((p) => p.presence === 'duty')
                .map((p) => p.userId),
            ),
          );
        })
        .catch(() => undefined);
    void load();
    const t = window.setInterval(() => {
      if (document.visibilityState === 'visible') void load();
    }, 30000);
    return () => {
      active = false;
      window.clearInterval(t);
    };
  }, [callsCabinetEnabled, selectedClientId]);

  const deleteEmployee = useDeleteEmployee(
    fetchEmployees,
    selectedClientId,
    searchText,
  );

  const toggleOrderDirection = () => {
    setOrderDirection((prev) => (prev === 'ASC' ? 'DESC' : 'ASC'));
    setPageIndex(0);
    fetchEmployees(0, true, searchText);
  };

  const handleDeleteClick = (employee: Employee) => {
    setEmployeeToDelete(employee);
    setShowConfirmModal(true);
  };

  const handleConfirmDelete = () => {
    if (employeeToDelete) {
      deleteEmployee(employeeToDelete.id);
      setShowConfirmModal(false);
      setEmployeeToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowConfirmModal(false);
    setEmployeeToDelete(null);
  };

  const handleCloseModal = () => {
    setPageIndex(0);
    setHasMore(true);
    setOrderDirection('ASC');
    setShowConfirmModal(false);
    setEmployeeToDelete(null);
    onClose();
  };

  if (selectedClientId === null) return null;

  return (
    <div
      className={embedded ? style.embeddedWrap : style.modalOverlay}
      onClick={embedded ? undefined : handleCloseModal}
    >
      <div
        className={`${style.modalContent} ${embedded ? style.modalEmbedded : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        {!embedded && (
          <ModalHeader
            selectedClientName={selectedClientName}
            onClose={handleCloseModal}
          />
        )}
        <SearchBar
          searchText={searchText}
          onSearchChange={handleSearchChange}
          onClearSearch={handleClearSearch}
        />
        {/* §FB81-R23: масовий режим «Відповідальна особа» прибрано — обидві ролі тепер
            перемикаються прямо в рядку співробітника і зберігаються миттєво. */}
        <div className={style.rolesHint}>
          <span>
            <b>Дзвінки з QR</b> — людина отримує виклики з віджета на телефон
            {callsCabinetEnabled
              ? " і може приймати їх у вебкабінеті викликів на комп'ютері. Увімкнення надсилає їй лист про вебкабінет."
              : '.'}
          </span>
          <span>
            <b>Вебкабінет</b> — хто планує зустрічі у вебкабінеті. Доступний,
            лише якщо у даних компанії увімкнено «Веб для відповідальних».
          </span>
          <span>
            <b>Адміністратор компанії</b> — бачить у вебкабінеті все, що
            стосується компанії: дзвінки, виклики, записи й протоколи,
            працівників (лише перегляд). Вмикається на звернення компанії;
            керівник компанії має роль автоматично.
          </span>
        </div>
        <div className={style.itemsList}>
          <div className={style.headerList}>
            <p>#</p>
            <div>
              <p onClick={toggleOrderDirection} className={style.infoChange}>
                Піб {orderDirection === 'ASC' ? <AzIcon /> : <ZaIcon />}
              </p>
            </div>
            <p>Email</p>
            <p>Телефон</p>
            <div className={style.headerToggles}>
              <p>Дзвінки з QR</p>
              <p>Вебкабінет</p>
              <p>Адміністратор компанії</p>
            </div>
          </div>
          <OwnerItem
            name={selectedClientName}
            email={ownerEmail}
            phone={ownerPhone}
            avatarUrl={ownerAvatarUrl}
          />
          {/* §ADM-3: isFetching — справжній СТАН, а не ref. Ref не викликає
              перемальовування, тож список бачив застаріле значення, просив
              сторінку двічі, і один із запитів мовчки губився разом із цілою
              сторінкою людей (у компанії на 91 особу показувало 51). */}
          <EmployeeList
            companyId={selectedClientId}
            companyWebLiaisonEnabled={companyWebLiaisonEnabled}
            onDutyIds={onDutyIds}
            employees={employees}
            hasMore={hasMore}
            isFetching={isFetching}
            onLoadMore={() => setPageIndex((prev) => prev + 1)}
            onDeleteClick={handleDeleteClick}
          />
        </div>
        <div className={style.wrapSubmit}>
          {/* §FB38: на сторінці «Закрити» немає що закривати (та ще й скидала
              сортування/пагінацію) — кнопка лишається тільки в модальному режимі. */}
          {!embedded && (
            <button onClick={handleCloseModal} className={style.submitButton}>
              Закрити
            </button>
          )}
        </div>
        {showConfirmModal && employeeToDelete && (
          <ConfirmDeleteModal
            userName={selectedClientName}
            avatarUrl={employeeToDelete.avatarUrl}
            onConfirm={handleConfirmDelete}
            onCancel={handleCancelDelete}
          />
        )}
      </div>
    </div>
  );
};

export default ModalWindowEmployees;
